import { useEffect, useState, useMemo } from "react";
import useSWR from "swr";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import clsx from "clsx";
import { Link } from "@remix-run/react";
import { Card } from "@/components/Card";
import { Calendar } from "@/components/Calendar";
import { Loading } from "@/components/Loader";
import type {
  EventV2,
  EventV2ListResponse,
  MicroCMSAPIContentResponse,
} from "@/lib/microcms.server";
import { sortEventsBySchedules } from "@/lib/microcms";
import { fetcher } from "@/lib/fetcher.client";

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

// ===== 外部依存のないユーティリティ関数 =====

function getLastMonthEnd(day: dayjs.Dayjs): string {
  return day.clone().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
}

function getNextMonthStart(day: dayjs.Dayjs): string {
  return day.clone().add(1, "month").startOf("month").format("YYYY-MM-DD");
}

function buildEventQuery(
  start: string,
  end: string,
  limit = 1000
): string {
  return `?filters=date_to_search[greater_than]${start}[and]date_to_search[less_than]${end}&limit=${limit}`;
}

function getMonthDays(date: dayjs.Dayjs): dayjs.Dayjs[] {
  const start = date.startOf("month");
  const end = date.endOf("month");
  const days: dayjs.Dayjs[] = [];
  let current = start;
  while (current.isBefore(end) || current.isSame(end, "day")) {
    days.push(current);
    current = current.add(1, "day");
  }
  return days;
}

// ===== 本体コンポーネント =====

export function HomeEvents() {
  const [day, setDay] = useState(dayjs());
  const [fetchQuery, setFetchQuery] = useState<string | null>(null);

  const { data: v2events, isValidating } = useSWR<
    EventV2ListResponse | null
  >(fetchQuery ? `/api/v2/events${fetchQuery}` : null, fetcher, {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    setFetchQuery(
      buildEventQuery(getLastMonthEnd(day), getNextMonthStart(day))
    );
  }, [day]);

  const sortedEvents = useMemo<
    MicroCMSAPIContentResponse<EventV2>[]
  >(() => {
    return v2events
      ? (sortEventsBySchedules(v2events.contents) as MicroCMSAPIContentResponse<
          EventV2
        >[])
      : [];
  }, [v2events]);

  const monthDays = useMemo(() => getMonthDays(day), [day]);
  const monthStart = monthDays[0];
  const monthEnd = monthDays[monthDays.length - 1];

  type DisplayItem =
    | {
        type: "public";
        start: dayjs.Dayjs;
        end: dayjs.Dayjs;
        event: MicroCMSAPIContentResponse<EventV2>;
      }
    | {
        type: "open";
        start: dayjs.Dayjs;
        end: dayjs.Dayjs;
      };

  const displayList = useMemo<DisplayItem[]>(() => {
    // 1) identify hidden days
    const hiddenDays = new Set<string>();
    sortedEvents.forEach((ev) => {
      const isHidden =
        ev.reservation_status?.includes("本予約完了") ||
        ev.reservation_status?.includes("仮予約中");
      if (isHidden) {
        ev.schedules.forEach((s) => {
          const d = dayjs(s.date);
          if (d.isBetween(monthStart, monthEnd, "day", "[]")) {
            hiddenDays.add(d.format("YYYY-MM-DD"));
          }
        });
      }
    });

    // 2) build public ranges
    const publicRanges = sortedEvents
      .filter((ev) => ev.reservation_status?.includes("一般公開"))
      .map((ev) => {
        const dates = ev.schedules
          .map((s) => dayjs(s.date))
          .filter((d) => d.isBetween(monthStart, monthEnd, "day", "[]"))
          .sort((a, b) => a.unix() - b.unix());
        if (!dates.length) return null;
        return {
          type: "public" as const,
          start: dates[0],
          end: dates[dates.length - 1],
          event: ev,
        };
      })
      .filter((x): x is Exclude<typeof x, null> => x !== null)
      .sort((a, b) => a.start.unix() - b.start.unix());

    // 3) fill gaps with open segments, skipping hidden days
    const segments: DisplayItem[] = [];
    let cursor = monthStart;

    publicRanges.forEach((range) => {
      const gapStart = cursor;
      const gapEnd = range.start.subtract(1, "day");
      if (gapStart.isSameOrBefore(gapEnd, "day")) {
        // split gap by hidden days
        let segStart = gapStart;
        let d = gapStart;
        while (d.isSameOrBefore(gapEnd, "day")) {
          if (hiddenDays.has(d.format("YYYY-MM-DD"))) {
            if (segStart.isBefore(d, "day")) {
              segments.push({ type: "open", start: segStart, end: d.subtract(1, "day") });
            }
            segStart = d.add(1, "day");
          }
          d = d.add(1, "day");
        }
        if (segStart.isSameOrBefore(gapEnd, "day")) {
          segments.push({ type: "open", start: segStart, end: gapEnd });
        }
      }
      // push the public range
      segments.push(range);
      cursor = range.end.add(1, "day");
    });

    // final gap till month end
    if (cursor.isSameOrBefore(monthEnd, "day")) {
      let segStart = cursor;
      let d = cursor;
      while (d.isSameOrBefore(monthEnd, "day")) {
        if (hiddenDays.has(d.format("YYYY-MM-DD"))) {
          if (segStart.isBefore(d, "day")) {
            segments.push({ type: "open", start: segStart, end: d.subtract(1, "day") });
          }
          segStart = d.add(1, "day");
        }
        d = d.add(1, "day");
      }
      if (segStart.isSameOrBefore(monthEnd, "day")) {
        segments.push({ type: "open", start: segStart, end: monthEnd });
      }
    }

    return segments;
  }, [sortedEvents, monthStart, monthEnd]);

  return (
    <Card className="lg:min-h-[700px] lg:max-h-[700px]">
      <div className="flex flex-col flex-grow">
        {/* カレンダー ヘッダー */}
        <div className="min-w-0 min-h-[36px]">
          <Calendar
            day={day}
            onDayChange={setDay}
            onCurrentMonthChange={setDay}
          >
            <Calendar.Control>
              <div className="flex justify-between items-center">
                <Calendar.Prev extend>
                  <button
                    type="button"
                    aria-label="前の月へ"
                    className="flex items-center justify-center p-2 rounded text-blue-600 hover:bg-interaction focus:bg-interaction"
                  >
                    <svg className="fill-current" viewBox="0 0 18 18" width="20" height="20">
                      <path
                        d="m13.7 16.29a1 1 0 1 1 -1.42 1.41l-8-8a1 1 0 0 1 0-1.41l8-8a1 1 0 1 1 1.42 1.41l-7.29 7.29z"
                        fillRule="evenodd"
                      />
                    </svg>
                  </button>
                </Calendar.Prev>
                <Calendar.Reset>
                  <Calendar.Label>
                    {({ day }) => <span className="text-lg font-bold">{day.format("YYYY年M月")}</span>}
                  </Calendar.Label>
                </Calendar.Reset>
                <Calendar.Next extend>
                  <button
                    type="button"
                    aria-label="次の月へ"
                    className="flex items-center justify-center p-2 rounded text-blue-600 hover:bg-interaction focus:bg-interaction"
                  >
                    <svg className="fill-current rotate-180" viewBox="0 0 18 18" width="20" height="20">
                      <path
                        d="m13.7 16.29a1 1 0 1 1 -1.42 1.41l-8-8a1 1 0 0 1 0-1.41l8-8a1 1 0 1 1 1.42 1.41l-7.29 7.29z"
                        fillRule="evenodd"
                      />
                    </svg>
                  </button>
                </Calendar.Next>
              </div>
            </Calendar.Control>
          </Calendar>
        </div>

        {/* イベント／開放日リスト */}
        <div className="min-w-0 flex-grow lg:max-h-[calc(700px-72px-theme(padding.6)*2)] overflow-auto">
          <div className="relative overflow-auto">
            <ul className="py-8 px-2">
              {displayList.map((item, idx) => {
                const key =
                  item.type === "public"
                    ? `${item.event.id}-${item.start.format("YYYY-MM-DD")}`
                    : `open-${item.start.format("YYYY-MM-DD")}-${item.end.format("YYYY-MM-DD")}-${idx}`;

                if (item.type === "public") {
                  const { event, start, end } = item;
                  const isRange = !start.isSame(end, "day");
                  return (
                    <li
                      key={key}
                      className={clsx("block mb-4", idx !== 0 && "pt-4 border-t border-t-gray-200")}
                    >
                      <Link to={`/events/article/${event.id}`} className="block hover:underline focus:underline">
                        <div className="space-y-2">
                          <span className="block text-sm font-bold text-brand-royalBlue">
                            {isRange
                              ? `${start.format("YYYY/M/D (dd)")} ~ ${end.format("YYYY/M/D (dd)")}`
                              : start.format("YYYY/M/D (dd)")}
                          </span>
                          <h3 className="font-black text-gray-900">{event.title}</h3>
                        </div>
                      </Link>
                    </li>
                  );
                }

                // open segment
                return (
                  <li
                    key={key}
                    className={clsx("block mb-4", idx !== 0 && "pt-4 border-t border-t-gray-200", "rounded")}
                  >
                    <div>
                      <span className="block text-xs text-gray-400 font-normal">
                        {item.start.isSame(item.end, "day")
                          ? `${item.start.format("YYYY/M/D (dd)")} 9:00 ~ 21:00`
                          : `${item.start.format("YYYY/M/D (dd)")} ~ ${item.end.format("YYYY/M/D (dd)")} 9:00 ~ 21:00`}
                      </span>
                      <h3 className="text-gray-400 font-normal mt-2">
                        一般開放日（どなたでもご利用可能です）
                      </h3>
                    </div>
                  </li>
                );
              })}
              {isValidating && <span className="spacer mt-12 ml-12" />}
            </ul>
            {isValidating && <Loading.Absolute />}
          </div>
        </div>

        {/* フッターリンク */}
        <div className="min-w-0 min-h-[36px] flex justify-end items-end w-full">
          <Link to="/vacancies" className="button button--plain">
            会場の予約状況を確認する
          </Link>
        </div>
      </div>
    </Card>
  );
}
